import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import _ from 'lodash';
import { Policy } from './Policy';
import { POLICY_CHANGE_HISTORY_STATUS, POLICY_CHANGE_EDITOR } from '../static/PolicyChangeHistoryStatus';
import { PolicyAmendmentProperty } from '../static/PolicyAmendmentProperty';

export enum ValueType {
  boolean,
  string,
  number,
  date,
  json,
}

// If a policy change is related to a collection (e.g. premiumBreakdown.flatExtras),
// each change is tracked with the following interface
// E.g. For a flat extra
// {uniquePropertyName: 'ruleCode', uniquePropertyValue: 'B00619', propBeingChanged:'premium', newValue: 85}
export interface CollectionItemChange {
  uniquePropertyName: string;
  uniquePropertyValue: string;
  propBeingChanged: string;
  newValue: number | string | boolean | Date;
}

@supertypeClass({ toClient: true, toServer: true })
export class PolicyChangeHistory extends Everything {
  static amendPolicy = function (
    policy: Policy,
    property: PolicyAmendmentProperty,
    value: string | number | boolean | Date,
    previousValue: string | number | boolean | Date,
    valueType: ValueType,
    status: POLICY_CHANGE_HISTORY_STATUS,
    details: string,
    editor?: string
  ): PolicyChangeHistory {
    function getPolicyChangeHistory(property) {
      let acquiredPCH;
      if (policy.policyChanges) {
        acquiredPCH = _.chain(policy.policyChanges)
          .filter({ status: POLICY_CHANGE_HISTORY_STATUS.PENDING, property: property })
          .sortBy('createdTime')
          .first()
          .value();
      }

      return acquiredPCH ? acquiredPCH : PolicyChangeHistory.new(policy);
    }

    const pch = getPolicyChangeHistory(property);

    pch.previousValue = previousValue;
    pch.property = property;
    pch.status = status ? status : POLICY_CHANGE_HISTORY_STATUS.PENDING;
    pch.details = details;
    pch.newValue = value;
    pch.valueType = valueType;
    pch.amendedDate = new Date();
    pch.editor = editor;

    return pch;
  };

  @property({ getType: () => Policy })
  policy: Policy;

  @property({ rule: ['required'], toServer: false })
  property: string;

  @property({ rule: ['required'], toServer: false })
  previousValue: string;

  @property({ toServer: false })
  newValue: string;

  @property({
    toServer: false,
    comment:
      'Indicates the type of the value that is being changed - boolean(0), string(1), number(2), date(3) or json(4)',
  })
  valueType: ValueType;

  @property({
    rule: ['required'],
  })
  status: POLICY_CHANGE_HISTORY_STATUS = POLICY_CHANGE_HISTORY_STATUS.PENDING;

  @property()
  details: string;

  @property()
  amendedDate: Date;

  @property()
  editor: string;

  constructor() {
    super();
  }

  static new(policy) {
    return new PolicyChangeHistory().init(policy);
  }

  init(policy) {
    this.policy = policy;
    return this;
  }

  getTypedNewValue(): string | number | boolean | Date | null {
    switch (this.valueType) {
      case ValueType.string:
        return !this.newValue || this.newValue === 'null' ? null : this.newValue; //already a string
      case ValueType.number:
        return parseFloat(this.newValue);
      case ValueType.boolean:
        return this.newValue === 'true' || (_.isBoolean(this.newValue) && this.newValue === true);
      case ValueType.date:
        return !this.newValue || this.newValue === 'null' ? null : new Date(this.newValue);
      default:
        throw Error(`Unable to handle valueType of ${this.valueType}`);
    }
  }
}
